import type { SxProps, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

export const FAMILY_SECTION_PAPER_SX: SxProps<Theme> = {
  borderRadius: 2,
  overflow: "hidden",
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "none",
};

/** Match ManageUserCircles / other dashboard tabs — no uppercase tracking. */
export const FAMILY_TAB_SX: SxProps<Theme> = {
  minHeight: { xs: 44, sm: 48 },
  "& .MuiTab-root": {
    minHeight: { xs: 44, sm: 48 },
    px: { xs: 0.75, sm: 2 },
    fontSize: { xs: "0.76rem", sm: "0.875rem" },
    fontWeight: 500,
    textTransform: "none",
    letterSpacing: 0,
    whiteSpace: "nowrap",
    color: "text.secondary",
  },
  "& .Mui-selected": {
    color: "primary.main",
    fontWeight: 500,
  },
  "& .MuiTabs-indicator": {
    height: 2,
  },
};

export const FAMILY_INFO_CARD_SX: SxProps<Theme> = {
  p: 2,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
  height: "100%",
  boxShadow: "none",
};

/** Primary annual statement card in the fee ledger — distinct from payment rows. */
export const FAMILY_LEDGER_STATEMENT_SX: SxProps<Theme> = {
  p: 1.5,
  pl: 1.75,
  borderRadius: 2,
  border: "1px solid",
  borderColor: (theme) => alpha(theme.palette.primary.main, 0.28),
  borderLeft: "4px solid",
  borderLeftColor: "primary.main",
  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
  boxShadow: "none",
};

/** Nested payment row under a ledger statement or charge. */
export const FAMILY_LEDGER_PAYMENT_SX: SxProps<Theme> = {
  p: 1.25,
  pl: 1.75,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
  boxShadow: "none",
  ml: { xs: 0.5, sm: 1.5 },
  borderLeft: "2px solid",
  borderLeftColor: (theme) => alpha(theme.palette.primary.main, 0.18),
};

/** Other fee accounts (events, extracurricular billing) — neutral but distinct from payments. */
export const FAMILY_FEE_ACCOUNT_SX: SxProps<Theme> = {
  p: 1.5,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: (theme) =>
    theme.palette.mode === "light" ? "grey.50" : "action.hover",
  boxShadow: "none",
};

export const FAMILY_SIDEBAR_ITEM_SX = (selected: boolean): SxProps<Theme> => ({
  p: 1.25,
  borderRadius: 2,
  border: "1px solid",
  borderColor: selected ? "primary.main" : "divider",
  bgcolor: selected ? "action.selected" : "background.paper",
  cursor: "pointer",
  transition: "border-color 0.15s ease, background-color 0.15s ease",
  "&:hover": {
    borderColor: selected ? "primary.main" : "action.focus",
  },
});

export const FAMILY_TEXT_LINK_SX: SxProps<Theme> = {
  p: 0,
  minWidth: 0,
  typography: "body2",
  fontWeight: 500,
  textTransform: "none",
  color: "primary.main",
  "&:hover": {
    bgcolor: "transparent",
    textDecoration: "underline",
  },
};

/** Lighter outlined inputs — match SearchField / dashboard divider borders. */
export const FAMILY_OUTLINED_FIELD_SX: SxProps<Theme> = {
  "& .MuiOutlinedInput-root": {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "divider",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "divider",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "primary.main",
      borderWidth: 1,
    },
  },
};

export const FAMILY_ACTIVITY_CHIP_SX: SxProps<Theme> = {
  height: "auto",
  py: 0.75,
  px: 1.25,
  borderRadius: 2,
  bgcolor: "action.hover",
  color: "text.primary",
  border: "none",
  "& .MuiChip-label": {
    px: 0.5,
    typography: "body2",
    whiteSpace: "normal",
  },
};
